import React, { useCallback, useEffect, useState } from 'react';

import { Button, Flex, Grid, GridItem, Spinner } from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import {
  Footer,
  Header,
  MemberCard,
  PageWrapper,
  SectionTitle,
  Spacer,
  Text,
} from '../../components';
import { useHeader } from '../../contexts/useHeader';
import { useNavigation } from '../../contexts/useNavigation';
import { aboutLinks, getTeamData } from '../../data/mock.data.about';
import { TeamData } from '../../models/Team';
import { TextType } from '../../models/Text';

import CollabGrid from './components/CollabGrid';
import TeamMemberModal from './components/TeamMemberModal';

const AboutUsPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { setScrollToAnchor } = useNavigation();
  const { headerDimensions } = useHeader();

  const [teamData, setTeamData] = useState<TeamData[]>([]);
  const [teamLoading, setTeamLoading] = useState<boolean>(true);

  const [memberOpen, setMemberOpen] = useState<boolean>(false);
  const [activeMember, setActiveMember] = useState<TeamData | null>(null);

  useEffect(() => {
    getTeamData()
      .then((items: TeamData[]) => setTeamData(items))
      .catch((err: unknown) => {
        // eslint-disable-next-line no-console
        console.error('Failed to load team from Cockpit:', err);
        setTeamData([]);
      })
      .finally(() => setTeamLoading(false));
  }, []);

  const openMember = useCallback((member: TeamData) => {
    setActiveMember(member);
    setMemberOpen(true);
  }, []);

  const closeMember = useCallback(() => {
    setMemberOpen(false);
    setActiveMember(null);
  }, []);

  const handleContactUs = () => {
    navigate('/');
    setScrollToAnchor('contact');
  };


  return (
    <>
      <Header links={aboutLinks} justLightMode />
      <PageWrapper>
        <Spacer size={headerDimensions?.height || 0} />
        <SectionTitle
          title={t('aboutUs.title')}
          description={t('aboutUs.description1')}
          description2={t('aboutUs.description2')}
        />

        {teamLoading ? (
          <Flex width="100%" justifyContent="center">
            <Spinner />
          </Flex>
        ) : null}

        {!teamLoading && teamData.length > 0 ? (
          <Grid
            flex={1}
            width="100%"
            templateColumns={{ base: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' }}
            gap={{ base: '8px', md: '25px' }}
          >
            {teamData.map((member: TeamData, index: number) => (
              <GridItem
                key={`item-${member.name}-${index}`}
                w="100%"
                aspectRatio={1}
              >
                <button
                  type="button"
                  onClick={() => openMember(member)}
                  style={{
                    width: '100%',
                    height: '100%',
                    padding: 0,
                    border: 0,
                    background: 'transparent',
                    cursor: 'pointer',
                  }}
                >
                  <MemberCard heroImage={member.image}>
                    <Flex
                      flexDirection="column"
                      alignItems="center"
                      justifyContent="flex-end"
                    >
                      <Text
                        color="gray.500"
                        type={{
                          base: TextType.Inter12Bold,
                          md: TextType.Inter18Bold,
                        }}
                      >
                        {member.name.toUpperCase()}
                      </Text>

                      <Spacer size={12} />

                      {member.role ? (
                        <>
                          <Text
                            type={{
                              base: TextType.Inter12Light,
                              md: TextType.Inter14Light,
                            }}
                            color="gray.500"
                            others={{ textAlign: 'center' }}
                          >
                            {member.role}
                          </Text>
                          <Spacer size={6} />
                        </>
                      ) : null}

                      {member.about ? (
                        <>
                          <Text
                            type={{
                              base: TextType.Inter12Light,
                              md: TextType.Inter14Light,
                            }}
                            color="gray.500"
                            others={{ textAlign: 'center' }}
                          >
                            {member.about}
                          </Text>
                          <Spacer size={6} />
                        </>
                      ) : null}
                    </Flex>
                  </MemberCard>
                </button>
              </GridItem>
            ))}
          </Grid>
        ) : null}

        <Spacer size={40} />
        <SectionTitle
          title={t('aboutUs.collabTitle')}
          description={t('aboutUs.collabDescription')}
        />
        <Spacer size={20} />
        <CollabGrid />
        <Spacer size={40} />

        <Flex
          justifyContent="center"
          alignItems="center"
          flexDirection="column"
          width="100%"
        >
          <Text type={{ base: TextType.Inter12Light, md: TextType.Inter14Light }}>
            {t('aboutUs.buttonMessage')}
          </Text>

          <Button
            variant="solid"
            bgColor="primary"
            marginTop="15px"
            width={{ base: '50%', md: '33.3333%' }}
            borderRadius={0}
            onClick={handleContactUs}
          >
            <Text
              type={{ base: TextType.Inter12Bold, md: TextType.Inter14Bold }}
              color="white"
            >
              {t('aboutUs.buttonTitle').toUpperCase()}
            </Text>
          </Button>
        </Flex>

        <Spacer size={20} />
        <Footer />
      </PageWrapper>

      <TeamMemberModal
        isOpen={memberOpen}
        onClose={closeMember}
        member={activeMember}
      />
    </>
  );
};

export default AboutUsPage;